import { ErrorHandler, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { defer, finalize, map, Observable, of, ReplaySubject, take, timeout } from 'rxjs';

/**
 * Upper bound on a guard's total wait, gate included: the clock starts when the guard is
 * subscribed, not when `dispatchWhen$` finally lets the request out. Chosen to sit above a
 * slow API call on a poor connection and below the point where a user assumes the tab is
 * broken: past it the navigation resolves to an error page rather than holding the splash
 * screen open forever.
 */
export const GUARD_ROUND_TRIP_TIMEOUT_MS = 15_000;

export class GuardTimeoutError extends Error {
  override readonly name = 'GuardTimeoutError';

  constructor(guardName: string, attemptedUrl: string, timeoutMs: number) {
    super(`${guardName} timed out after ${timeoutMs}ms while navigating to ${attemptedUrl}`);
  }
}

export interface GuardRoundTripConfig {
  /** Identifies the guard in the Sentry report, so name it after the guard. */
  readonly guardName: string;
  /** Invoked once `outcome$` is subscribed, never before. */
  readonly dispatchRequest: () => void;
  /**
   * Settles the guard on its first emission: a `UrlTree` redirects, anything else passes.
   * Merge the store hit, the success action and the failure action into this one stream, so
   * that a source deciding both ways — an action carrying the verdict in its payload — is
   * subscribed once rather than filtered twice.
   */
  readonly outcome$: Observable<unknown>;
  readonly onTimeout: () => UrlTree;
  readonly dispatchWhen$?: Observable<unknown>;
  readonly timeoutMs?: number;
}

/**
 * Runs a guard's dispatch-and-wait against a deadline. Without one, an action that never
 * arrives — a dropped effect, an unmatched failure action, a cancelled inner request —
 * leaves the router pending with no error, no telemetry and no way back for the user.
 *
 * Every subscription dispatches its own request. The router subscribes once, so any other
 * caller that needs one shared round trip has to add its own shareReplay.
 */
export const guardRoundTrip = ({
  guardName,
  dispatchRequest,
  outcome$,
  onTimeout,
  dispatchWhen$,
  timeoutMs = GUARD_ROUND_TRIP_TIMEOUT_MS,
}: GuardRoundTripConfig): Observable<true | UrlTree> => {
  const errorHandler = inject(ErrorHandler);
  const attemptedUrl = getAttemptedUrl(inject(Router));

  return defer(() => {
    // The action feed is hot, and an effect can answer synchronously. Buffering the outcome
    // lets the stream go live before the request leaves, so a same-tick reply is still seen.
    const settled = new ReplaySubject<true | UrlTree>(1);

    const outcomeSubscription = outcome$
      .pipe(
        take(1),
        map((outcome) => (outcome instanceof UrlTree ? outcome : (true as const))),
        timeout({
          first: timeoutMs,
          with: () => {
            errorHandler.handleError(new GuardTimeoutError(guardName, attemptedUrl, timeoutMs));
            return of(onTimeout());
          },
        })
      )
      .subscribe(settled);

    const requestSubscription = (dispatchWhen$ ?? of(true))
      .pipe(take(1))
      .subscribe(() => dispatchRequest());

    // The router drops a guard's observable when a navigation is superseded. Without this
    // the deadline would still fire and report a timeout the user never waited for.
    return settled.pipe(
      finalize(() => {
        outcomeSubscription.unsubscribe();
        requestSubscription.unsubscribe();
      })
    );
  });
};

const getAttemptedUrl = (router: Router): string => {
  const navigation = router.getCurrentNavigation();
  const attemptedUrl = navigation?.finalUrl ?? navigation?.extractedUrl;

  return attemptedUrl ? attemptedUrl.toString() : router.url;
};
